import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Settings, Users, Package, CreditCard, Gift, LogOut, DollarSign,
    Coins, PauseCircle, PlayCircle, Loader2, RefreshCw, AlertTriangle,
    Eye, X, ChevronDown, ChevronUp, Wallet, UserCircle, FileText, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAdmin } from '../contexts/AdminContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
    const navigate = useNavigate();
    const {
        isAuthenticated, logout, loading, token,
        getSettings, updateSettings, pauseICO, resumeICO,
        getOffers, createOffer, updateOffer, deleteOffer,
        getOrders, getTransactions, getReferrals, updateReferralStatus,
        getStats, getUsers
    } = useAdmin();
    
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [offers, setOffers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [users, setUsers] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [refreshing, setRefreshing] = useState(false);
    
    // User details modal
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    
    // Settings form
    const [settingsForm, setSettingsForm] = useState({
        gold_price_per_gram: '',
        pio_gold_ratio: '',
        ico_wallet_address: '',
        encrypted_private_key: '',
        whitepaper_url: ''
    });
    
    // New offer form
    const [offerForm, setOfferForm] = useState({
        min_usdt: '',
        max_usdt: '',
        discount_percent: '',
        validity_days: '',
        is_active: true
    });
    
    // Team members
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamForm, setTeamForm] = useState({
        role: 'CEO',
        name: '',
        photo_url: ''
    });
    
    // Legal documents
    const [legalDocs, setLegalDocs] = useState([]);
    const [legalForm, setLegalForm] = useState({
        title: '',
        slug: '',
        content: '',
        is_active: true
    });
    
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }
        loadData();
    }, [isAuthenticated, navigate]);
    
    const loadData = async () => {
        setRefreshing(true);
        try {
            const [settingsData, statsData, offersData, ordersData, txData, refData, usersData] = await Promise.all([
                getSettings(),
                getStats(),
                getOffers(),
                getOrders(),
                getTransactions(),
                getReferrals(),
                getUsers()
            ]);
            
            // Load team and legal docs
            const teamRes = await axios.get(`${API_URL}/admin/team`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const legalRes = await axios.get(`${API_URL}/admin/legal`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setTeamMembers(teamRes.data || []);
            setLegalDocs(legalRes.data || []);
            
            setSettings(settingsData);
            setStats(statsData);
            setOffers(offersData);
            setOrders(ordersData);
            setTransactions(txData);
            setReferrals(refData);
            setUsers(usersData);
            
            setSettingsForm({
                gold_price_per_gram: settingsData.gold_price_per_gram,
                pio_gold_ratio: settingsData.pio_gold_ratio || 0.132,
                ico_wallet_address: settingsData.ico_wallet_address,
                encrypted_private_key: '',
                whitepaper_url: settingsData.whitepaper_url || ''
            });
        } catch (err) {
            toast.error('Error loading data');
        } finally {
            setRefreshing(false);
        }
    };
    
    const fetchUserDetails = async (userId) => {
        try {
            setLoadingUser(true);
            setSelectedUser(userId);
            const response = await axios.get(`${API_URL}/admin/users/${userId}/details`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserDetails(response.data);
        } catch (err) {
            toast.error('Failed to load user details');
            setSelectedUser(null);
        } finally {
            setLoadingUser(false);
        }
    };
    
    const closeUserModal = () => {
        setSelectedUser(null);
        setUserDetails(null);
    };
    
    const handleSaveSettings = async () => {
        try {
            const updates = {};
            if (settingsForm.gold_price_per_gram) {
                updates.gold_price_per_gram = parseFloat(settingsForm.gold_price_per_gram);
            }
            if (settingsForm.pio_gold_ratio) {
                updates.pio_gold_ratio = parseFloat(settingsForm.pio_gold_ratio);
            }
            if (settingsForm.ico_wallet_address) {
                updates.ico_wallet_address = settingsForm.ico_wallet_address;
            }
            if (settingsForm.encrypted_private_key) {
                updates.encrypted_private_key = settingsForm.encrypted_private_key;
            }
            if (settingsForm.whitepaper_url !== undefined) {
                updates.whitepaper_url = settingsForm.whitepaper_url;
            }
            
            await updateSettings(updates);
            toast.success('Settings updated!');
            loadData();
        } catch (err) {
            toast.error('Failed to update settings');
        }
    };
    
    const handleToggleICO = async () => {
        try {
            if (settings?.ico_active) {
                await pauseICO();
                toast.warning('ICO Paused');
            } else {
                await resumeICO();
                toast.success('ICO Resumed');
            }
            loadData();
        } catch (err) {
            toast.error('Failed to toggle ICO');
        }
    };
    
    const handleCreateOffer = async (e) => {
        e.preventDefault();
        try {
            await createOffer({
                min_usdt: parseFloat(offerForm.min_usdt),
                max_usdt: parseFloat(offerForm.max_usdt),
                discount_percent: parseFloat(offerForm.discount_percent),
                validity_days: parseInt(offerForm.validity_days),
                is_active: offerForm.is_active
            });
            toast.success('Offer created!');
            setOfferForm({ min_usdt: '', max_usdt: '', discount_percent: '', validity_days: '', is_active: true });
            loadData();
        } catch (err) {
            toast.error('Failed to create offer');
        }
    };
    
    const handleDeleteOffer = async (id) => {
        if (!window.confirm('Delete this offer?')) return;
        try {
            await deleteOffer(id);
            toast.success('Offer deleted');
            loadData();
        } catch (err) {
            toast.error('Failed to delete offer');
        }
    };
    
    const handleReferralAction = async (id, status) => {
        try {
            await updateReferralStatus(id, status);
            toast.success(`Referral ${status}`);
            loadData();
        } catch (err) {
            toast.error('Failed to update referral');
        }
    };
    
    // Team handlers
    const handleSaveTeamMember = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/admin/team`, teamForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`${teamForm.role} saved!`);
            setTeamForm({ role: 'CEO', name: '', photo_url: '' });
            loadData();
        } catch (err) {
            toast.error('Failed to save team member');
        }
    };
    
    const handleDeleteTeamMember = async (role) => {
        if (!window.confirm(`Delete ${role}?`)) return;
        try {
            await axios.delete(`${API_URL}/admin/team/${role}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`${role} deleted`);
            loadData();
        } catch (err) {
            toast.error('Failed to delete team member');
        }
    };
    
    // Legal handlers
    const handleSaveLegalDoc = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/admin/legal`, legalForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Document saved!`);
            setLegalForm({ title: '', slug: '', content: '', is_active: true });
            loadData();
        } catch (err) {
            toast.error('Failed to save document');
        }
    };
    
    const handleDeleteLegalDoc = async (slug) => {
        if (!window.confirm(`Delete ${slug}?`)) return;
        try {
            await axios.delete(`${API_URL}/admin/legal/${slug}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Document deleted`);
            loadData();
        } catch (err) {
            toast.error('Failed to delete document');
        }
    };
    
    const handleLogout = () => {
        logout();
        navigate('/admin');
    };
    
    if (!isAuthenticated) {
        return null;
    }
    
    return (
        <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
            {/* Admin Navbar */}
            <nav className="bg-obsidian-light border-b border-zinc-800 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Link to="/" className="flex items-center space-x-2">
                            <img src="/logo.png" alt="PIOGOLD" className="w-8 h-8 object-contain" />
                            <span className="text-lg font-bold font-serif gold-gradient-text">PIOGOLD Admin</span>
                        </Link>
                    </div>
                    <div className="flex items-center space-x-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadData}
                            disabled={refreshing}
                            className="text-zinc-400 hover:text-gold"
                        >
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLogout}
                            className="text-zinc-400 hover:text-red-500"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </nav>
            
            <div className="p-6">
                {/* Stats Overview */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card className="glass-card border-zinc-800">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-zinc-400 text-sm">Total Users</p>
                                    <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
                                </div>
                                <Users className="w-8 h-8 text-gold" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="glass-card border-zinc-800">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-zinc-400 text-sm">USDT Raised</p>
                                    <p className="text-2xl font-bold text-green-500">${stats?.total_usdt_raised?.toFixed(2) || 0}</p>
                                </div>
                                <DollarSign className="w-8 h-8 text-green-500" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="glass-card border-gold/30">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-zinc-400 text-sm">PIO Sold</p>
                                    <p className="text-2xl font-bold text-gold">{stats?.total_pio_sold?.toFixed(4) || 0}</p>
                                </div>
                                <Coins className="w-8 h-8 text-gold" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="glass-card border-zinc-800">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-zinc-400 text-sm">Pending Referrals</p>
                                    <p className="text-2xl font-bold text-yellow-500">{stats?.pending_referrals || 0}</p>
                                </div>
                                <Gift className="w-8 h-8 text-yellow-500" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-zinc-900 border border-zinc-800">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                        </TabsTrigger>
                        <TabsTrigger value="users" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <Users className="w-4 h-4 mr-2" />
                            Users
                        </TabsTrigger>
                        <TabsTrigger value="offers" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <Gift className="w-4 h-4 mr-2" />
                            Offers
                        </TabsTrigger>
                        <TabsTrigger value="orders" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <Package className="w-4 h-4 mr-2" />
                            Orders
                        </TabsTrigger>
                        <TabsTrigger value="referrals" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <Gift className="w-4 h-4 mr-2" />
                            Referrals
                        </TabsTrigger>
                        <TabsTrigger value="transactions" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Transactions
                        </TabsTrigger>
                        <TabsTrigger value="team" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <UserCircle className="w-4 h-4 mr-2" />
                            Team
                        </TabsTrigger>
                        <TabsTrigger value="legal" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                            <FileText className="w-4 h-4 mr-2" />
                            Legal
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* Settings Tab */}
                    <TabsContent value="overview">
                        <div className="grid lg:grid-cols-2 gap-6">
                            {/* ICO Control */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white flex items-center justify-between">
                                        ICO Control
                                        <Button
                                            onClick={handleToggleICO}
                                            variant={settings?.ico_active ? 'destructive' : 'default'}
                                            className={settings?.ico_active ? '' : 'gold-gradient text-black'}
                                            data-testid="toggle-ico-btn"
                                        >
                                            {settings?.ico_active ? (
                                                <><PauseCircle className="w-4 h-4 mr-2" /> Pause ICO</>
                                            ) : (
                                                <><PlayCircle className="w-4 h-4 mr-2" /> Resume ICO</>
                                            )}
                                        </Button>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
                                        <span className="text-zinc-400">ICO Status</span>
                                        <span className={`font-semibold ${settings?.ico_active ? 'text-green-500' : 'text-red-500'}`}>
                                            {settings?.ico_active ? 'ACTIVE' : 'PAUSED'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
                                        <span className="text-zinc-400">Private Key</span>
                                        <span className={`font-semibold ${settings?.has_private_key ? 'text-green-500' : 'text-red-500'}`}>
                                            {settings?.has_private_key ? 'Configured' : 'Not Set'}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            {/* Gold Price */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Pricing Settings</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Gold Price per Gram (USD)</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={settingsForm.gold_price_per_gram}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, gold_price_per_gram: e.target.value })}
                                                className="bg-zinc-900/50 border-zinc-800"
                                                data-testid="gold-price-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">PIO Gold Ratio (grams per PIO)</Label>
                                            <Input
                                                type="number"
                                                step="0.001"
                                                value={settingsForm.pio_gold_ratio}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, pio_gold_ratio: e.target.value })}
                                                className="bg-zinc-900/50 border-zinc-800"
                                                data-testid="pio-gold-ratio-input"
                                            />
                                            <p className="text-xs text-zinc-500">1 PIO = {settingsForm.pio_gold_ratio || 0.132} grams of gold = ${((parseFloat(settingsForm.gold_price_per_gram) || 0) * (parseFloat(settingsForm.pio_gold_ratio) || 0.132)).toFixed(2)} USD</p>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={handleSaveSettings}
                                        className="gold-gradient text-black font-semibold w-full"
                                        data-testid="save-settings-btn"
                                    >
                                        Save Pricing Settings
                                    </Button>
                                </CardContent>
                            </Card>
                            
                            {/* Wallet Settings */}
                            <Card className="glass-card border-zinc-800 lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Wallet Configuration</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">ICO Wallet Address (receives USDT)</Label>
                                            <Input
                                                type="text"
                                                placeholder="0x..."
                                                value={settingsForm.ico_wallet_address}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, ico_wallet_address: e.target.value })}
                                                className="bg-zinc-900/50 border-zinc-800 font-mono text-sm"
                                                data-testid="ico-wallet-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Admin Private Key (for PIO transfers)</Label>
                                            <Input
                                                type="password"
                                                placeholder="Enter to update (will be encrypted)"
                                                value={settingsForm.encrypted_private_key}
                                                onChange={(e) => setSettingsForm({ ...settingsForm, encrypted_private_key: e.target.value })}
                                                className="bg-zinc-900/50 border-zinc-800 font-mono text-sm"
                                                data-testid="private-key-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                                        <div className="flex items-start space-x-2">
                                            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                            <p className="text-yellow-500 text-sm">
                                                <strong>Security:</strong> Private key is encrypted with AES-256 before storage. 
                                                Never share your private key. This wallet will be used to send PIO to buyers.
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <Button
                                        onClick={handleSaveSettings}
                                        className="gold-gradient text-black font-semibold"
                                    >
                                        Save Wallet Settings
                                    </Button>
                                </CardContent>
                            </Card>
                            
                            {/* Whitepaper Settings */}
                            <Card className="glass-card border-zinc-800 lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Whitepaper Configuration</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label className="text-zinc-400">Whitepaper URL</Label>
                                        <Input
                                            type="url"
                                            placeholder="https://example.com/whitepaper.pdf"
                                            value={settingsForm.whitepaper_url}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, whitepaper_url: e.target.value })}
                                            className="bg-zinc-900/50 border-zinc-800"
                                            data-testid="whitepaper-url-input"
                                        />
                                        <p className="text-xs text-zinc-500">Link to your whitepaper document (displayed on homepage)</p>
                                    </div>
                                    <Button
                                        onClick={handleSaveSettings}
                                        className="gold-gradient text-black font-semibold"
                                    >
                                        Save Whitepaper URL
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                    
                    {/* Users Tab */}
                    <TabsContent value="users">
                        <Card className="glass-card border-zinc-800">
                            <CardHeader>
                                <CardTitle className="font-serif text-white">All Users ({users.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Date</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Wallet</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Referral Code</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Total USDT</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Total PIO</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Direct Team</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                                                    <td className="py-3 px-2 text-white text-sm">
                                                        {new Date(user.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-2 text-zinc-300 font-mono text-sm">
                                                        <a 
                                                            href={`https://bscscan.com/address/${user.wallet_address}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="hover:text-gold"
                                                        >
                                                            {user.wallet_address?.slice(0, 8)}...{user.wallet_address?.slice(-6)}
                                                        </a>
                                                    </td>
                                                    <td className="py-3 px-2 text-gold font-mono text-sm">
                                                        {user.referral_code}
                                                    </td>
                                                    <td className="py-3 px-2 text-white font-mono text-sm">
                                                        ${user.total_purchased_usdt?.toFixed(2) || '0.00'}
                                                    </td>
                                                    <td className="py-3 px-2 text-gold font-mono text-sm">
                                                        {user.total_pio_received?.toFixed(4) || '0.0000'}
                                                    </td>
                                                    <td className="py-3 px-2 text-white text-sm">
                                                        {user.direct_referrals || 0}
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => fetchUserDetails(user.id)}
                                                            className="text-gold hover:text-gold-light"
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" /> View
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {users.length === 0 && (
                                        <p className="text-zinc-500 text-center py-8">No users yet</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    {/* Offers Tab */}
                    <TabsContent value="offers">
                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* Create Offer */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Create Offer</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleCreateOffer} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-zinc-400">Min USDT</Label>
                                                <Input
                                                    type="number"
                                                    value={offerForm.min_usdt}
                                                    onChange={(e) => setOfferForm({ ...offerForm, min_usdt: e.target.value })}
                                                    className="bg-zinc-900/50 border-zinc-800"
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-zinc-400">Max USDT</Label>
                                                <Input
                                                    type="number"
                                                    value={offerForm.max_usdt}
                                                    onChange={(e) => setOfferForm({ ...offerForm, max_usdt: e.target.value })}
                                                    className="bg-zinc-900/50 border-zinc-800"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-zinc-400">Discount %</Label>
                                                <Input
                                                    type="number"
                                                    value={offerForm.discount_percent}
                                                    onChange={(e) => setOfferForm({ ...offerForm, discount_percent: e.target.value })}
                                                    className="bg-zinc-900/50 border-zinc-800"
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-zinc-400">Validity (days)</Label>
                                                <Input
                                                    type="number"
                                                    value={offerForm.validity_days}
                                                    onChange={(e) => setOfferForm({ ...offerForm, validity_days: e.target.value })}
                                                    className="bg-zinc-900/50 border-zinc-800"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                checked={offerForm.is_active}
                                                onCheckedChange={(checked) => setOfferForm({ ...offerForm, is_active: checked })}
                                            />
                                            <Label className="text-zinc-400">Active</Label>
                                        </div>
                                        <Button type="submit" className="w-full gold-gradient text-black font-semibold">
                                            Create Offer
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            
                            {/* Existing Offers */}
                            <Card className="glass-card border-zinc-800 lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Existing Offers</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {offers.map((offer) => (
                                            <div
                                                key={offer.id}
                                                className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800"
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <div className={`w-3 h-3 rounded-full ${offer.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                                                    <div>
                                                        <p className="text-white font-medium">
                                                            ${offer.min_usdt} - ${offer.max_usdt}
                                                        </p>
                                                        <p className="text-zinc-500 text-sm">
                                                            Valid for {offer.validity_days} days
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-4">
                                                    <span className="text-gold font-bold text-xl">+{offer.discount_percent}%</span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteOffer(offer.id)}
                                                        className="text-red-500 hover:text-red-400"
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {offers.length === 0 && (
                                            <p className="text-zinc-500 text-center py-8">No offers created yet</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                    
                    {/* Orders Tab */}
                    <TabsContent value="orders">
                        <Card className="glass-card border-zinc-800">
                            <CardHeader>
                                <CardTitle className="font-serif text-white">All Orders ({orders.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Date</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Wallet</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">USDT</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">PIO</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Bonus</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.slice(0, 50).map((order) => (
                                                <tr key={order.id} className="border-b border-zinc-800/50">
                                                    <td className="py-3 px-2 text-white text-sm">
                                                        {new Date(order.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-2 text-zinc-300 font-mono text-sm">
                                                        {order.wallet_address?.slice(0, 8)}...
                                                    </td>
                                                    <td className="py-3 px-2 text-white font-mono text-sm">
                                                        ${order.usdt_amount}
                                                    </td>
                                                    <td className="py-3 px-2 text-gold font-mono text-sm">
                                                        {order.total_pio?.toFixed(4)}
                                                    </td>
                                                    <td className="py-3 px-2 text-green-500 text-sm">
                                                        {order.discount_percent > 0 ? `+${order.discount_percent}%` : '-'}
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className={`text-sm px-2 py-1 rounded ${
                                                            order.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                                                            order.status.includes('failed') ? 'bg-red-500/20 text-red-500' :
                                                            'bg-yellow-500/20 text-yellow-500'
                                                        }`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {orders.length === 0 && (
                                        <p className="text-zinc-500 text-center py-8">No orders yet</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    {/* Referrals Tab */}
                    <TabsContent value="referrals">
                        <Card className="glass-card border-zinc-800">
                            <CardHeader>
                                <CardTitle className="font-serif text-white">Referral Rewards ({referrals.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Date</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Level</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">USDT Base</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Reward (PIO)</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Status</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {referrals.slice(0, 50).map((ref) => (
                                                <tr key={ref.id} className="border-b border-zinc-800/50">
                                                    <td className="py-3 px-2 text-white text-sm">
                                                        {new Date(ref.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-2 text-gold font-semibold">
                                                        Level {ref.level}
                                                    </td>
                                                    <td className="py-3 px-2 text-white font-mono text-sm">
                                                        ${ref.usdt_amount}
                                                    </td>
                                                    <td className="py-3 px-2 text-gold font-mono text-sm">
                                                        {ref.reward_pio?.toFixed(6)}
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className={`text-sm px-2 py-1 rounded ${
                                                            ref.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                                                            ref.status === 'approved' ? 'bg-blue-500/20 text-blue-500' :
                                                            ref.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                                                            'bg-yellow-500/20 text-yellow-500'
                                                        }`}>
                                                            {ref.status}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2 space-x-2">
                                                        {ref.status === 'pending' && (
                                                            <>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => handleReferralAction(ref.id, 'approved')}
                                                                    className="text-green-500 hover:text-green-400 text-xs"
                                                                >
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => handleReferralAction(ref.id, 'rejected')}
                                                                    className="text-red-500 hover:text-red-400 text-xs"
                                                                >
                                                                    Reject
                                                                </Button>
                                                            </>
                                                        )}
                                                        {ref.status === 'approved' && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleReferralAction(ref.id, 'paid')}
                                                                className="text-gold hover:text-gold-light text-xs"
                                                            >
                                                                Mark Paid
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {referrals.length === 0 && (
                                        <p className="text-zinc-500 text-center py-8">No referral rewards yet</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    {/* Transactions Tab */}
                    <TabsContent value="transactions">
                        <Card className="glass-card border-zinc-800">
                            <CardHeader>
                                <CardTitle className="font-serif text-white">All Transactions ({transactions.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-zinc-800">
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Date</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Type</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Chain</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Amount</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">TX Hash</th>
                                                <th className="text-left py-3 px-2 text-zinc-400 text-sm">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.slice(0, 50).map((tx) => (
                                                <tr key={tx.id} className="border-b border-zinc-800/50">
                                                    <td className="py-3 px-2 text-white text-sm">
                                                        {new Date(tx.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-2 text-zinc-300 text-sm capitalize">
                                                        {tx.type.replace(/_/g, ' ')}
                                                    </td>
                                                    <td className="py-3 px-2 text-sm">
                                                        <span className={tx.chain === 'bsc' ? 'text-yellow-500' : 'text-gold'}>
                                                            {tx.chain.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2 text-white font-mono text-sm">
                                                        {tx.amount}
                                                    </td>
                                                    <td className="py-3 px-2 text-zinc-400 font-mono text-xs">
                                                        {tx.tx_hash?.slice(0, 16)}...
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <span className={`text-sm px-2 py-1 rounded ${
                                                            tx.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                                                            tx.status === 'failed' ? 'bg-red-500/20 text-red-500' :
                                                            'bg-yellow-500/20 text-yellow-500'
                                                        }`}>
                                                            {tx.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {transactions.length === 0 && (
                                        <p className="text-zinc-500 text-center py-8">No transactions yet</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    
                    {/* Team Tab */}
                    <TabsContent value="team">
                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* Add/Edit Team Member */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Add/Edit Team Member</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSaveTeamMember} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Role</Label>
                                            <select
                                                value={teamForm.role}
                                                onChange={(e) => setTeamForm({ ...teamForm, role: e.target.value })}
                                                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-md h-10 px-3 text-white"
                                            >
                                                <option value="CEO">CEO</option>
                                                <option value="CFO">CFO</option>
                                                <option value="COO">COO</option>
                                                <option value="Marketing Head">Marketing Head</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Name</Label>
                                            <Input
                                                value={teamForm.name}
                                                onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                                                placeholder="John Doe"
                                                className="bg-zinc-900/50 border-zinc-800"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Photo URL</Label>
                                            <Input
                                                value={teamForm.photo_url}
                                                onChange={(e) => setTeamForm({ ...teamForm, photo_url: e.target.value })}
                                                placeholder="https://example.com/photo.jpg"
                                                className="bg-zinc-900/50 border-zinc-800"
                                            />
                                        </div>
                                        <Button type="submit" className="w-full gold-gradient text-black font-semibold">
                                            Save Team Member
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            
                            {/* Current Team */}
                            <Card className="glass-card border-zinc-800 lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Current Team ({teamMembers.length})</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {teamMembers.length > 0 ? (
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            {teamMembers.map((member) => (
                                                <div
                                                    key={member.id}
                                                    className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800"
                                                >
                                                    <div className="flex items-center space-x-4">
                                                        {member.photo_url ? (
                                                            <img 
                                                                src={member.photo_url} 
                                                                alt={member.name}
                                                                className="w-12 h-12 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                                                                <UserCircle className="w-6 h-6 text-gold" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="text-white font-medium">{member.name}</p>
                                                            <p className="text-gold text-sm">{member.role}</p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteTeamMember(member.role)}
                                                        className="text-red-500 hover:text-red-400"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 text-center py-8">No team members added yet</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                    
                    {/* Legal Tab */}
                    <TabsContent value="legal">
                        <div className="grid lg:grid-cols-2 gap-6">
                            {/* Add/Edit Legal Document */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Add/Edit Legal Document</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSaveLegalDoc} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Title</Label>
                                            <Input
                                                value={legalForm.title}
                                                onChange={(e) => setLegalForm({ ...legalForm, title: e.target.value })}
                                                placeholder="Terms of Service"
                                                className="bg-zinc-900/50 border-zinc-800"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Slug (URL path)</Label>
                                            <Input
                                                value={legalForm.slug}
                                                onChange={(e) => setLegalForm({ ...legalForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                                placeholder="terms-of-service"
                                                className="bg-zinc-900/50 border-zinc-800"
                                                required
                                            />
                                            <p className="text-zinc-600 text-xs">Will be accessible at /legal/{legalForm.slug || 'slug'}</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-zinc-400">Content</Label>
                                            <textarea
                                                value={legalForm.content}
                                                onChange={(e) => setLegalForm({ ...legalForm, content: e.target.value })}
                                                placeholder="Enter document content..."
                                                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-md p-3 text-white min-h-[200px] focus:border-gold focus:outline-none"
                                                required
                                            />
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                checked={legalForm.is_active}
                                                onCheckedChange={(checked) => setLegalForm({ ...legalForm, is_active: checked })}
                                            />
                                            <Label className="text-zinc-400">Active</Label>
                                        </div>
                                        <Button type="submit" className="w-full gold-gradient text-black font-semibold">
                                            Save Document
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                            
                            {/* Current Documents */}
                            <Card className="glass-card border-zinc-800">
                                <CardHeader>
                                    <CardTitle className="font-serif text-white">Legal Documents ({legalDocs.length})</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {legalDocs.length > 0 ? (
                                        <div className="space-y-3">
                                            {legalDocs.map((doc) => (
                                                <div
                                                    key={doc.id}
                                                    className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800"
                                                >
                                                    <div>
                                                        <p className="text-white font-medium">{doc.title}</p>
                                                        <p className="text-zinc-500 text-sm">/legal/{doc.slug}</p>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <span className={`text-xs px-2 py-1 rounded ${doc.is_active ? 'bg-green-500/20 text-green-500' : 'bg-zinc-700 text-zinc-400'}`}>
                                                            {doc.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setLegalForm({
                                                                title: doc.title,
                                                                slug: doc.slug,
                                                                content: doc.content || '',
                                                                is_active: doc.is_active
                                                            })}
                                                            className="text-gold hover:text-gold-light"
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteLegalDoc(doc.slug)}
                                                            className="text-red-500 hover:text-red-400"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 text-center py-8">No legal documents yet. Add Terms, Privacy Policy, etc.</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
            
            {/* User Details Modal */}
            <AnimatePresence>
                {selectedUser && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={closeUserModal}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-obsidian-light border border-zinc-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {loadingUser ? (
                                <div className="p-8 text-center">
                                    <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto" />
                                    <p className="text-zinc-400 mt-4">Loading user details...</p>
                                </div>
                            ) : userDetails ? (
                                <>
                                    {/* Modal Header */}
                                    <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                                        <div>
                                            <h2 className="text-xl font-bold text-white">User Details</h2>
                                            <p className="text-zinc-400 font-mono text-sm mt-1">
                                                {userDetails.user.wallet_address}
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={closeUserModal}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                    
                                    {/* Modal Content */}
                                    <div className="p-6 space-y-6">
                                        {/* User Stats */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-zinc-900/50 rounded-lg p-4">
                                                <p className="text-zinc-400 text-sm">Referral Code</p>
                                                <p className="text-gold font-mono text-lg font-bold">{userDetails.user.referral_code}</p>
                                            </div>
                                            <div className="bg-zinc-900/50 rounded-lg p-4">
                                                <p className="text-zinc-400 text-sm">Total Spent</p>
                                                <p className="text-white text-lg font-bold">${userDetails.user.total_purchased_usdt?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <div className="bg-zinc-900/50 rounded-lg p-4">
                                                <p className="text-zinc-400 text-sm">PIO Received</p>
                                                <p className="text-gold text-lg font-bold">{userDetails.user.total_pio_received?.toFixed(4) || '0.0000'}</p>
                                            </div>
                                            <div className="bg-zinc-900/50 rounded-lg p-4">
                                                <p className="text-zinc-400 text-sm">Joined</p>
                                                <p className="text-white text-lg font-bold">{new Date(userDetails.user.created_at).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        
                                        {/* Referrer Info */}
                                        {userDetails.referrer && (
                                            <div className="bg-zinc-900/50 rounded-lg p-4">
                                                <p className="text-zinc-400 text-sm mb-2">Referred By</p>
                                                <p className="text-white font-mono text-sm">{userDetails.referrer.wallet_address}</p>
                                                <p className="text-gold text-sm">Code: {userDetails.referrer.referral_code}</p>
                                            </div>
                                        )}
                                        
                                        {/* Team Stats */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                                <Users className="w-5 h-5 mr-2 text-gold" />
                                                Team Overview (Total: {userDetails.team.total_team})
                                            </h3>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 text-center">
                                                    <p className="text-gold text-2xl font-bold">{userDetails.team.level1.count}</p>
                                                    <p className="text-zinc-400 text-sm">Level 1 (Direct)</p>
                                                    <p className="text-green-500 text-xs mt-1">10% Commission</p>
                                                </div>
                                                <div className="bg-zinc-900/50 border border-zinc-700 rounded-lg p-4 text-center">
                                                    <p className="text-white text-2xl font-bold">{userDetails.team.level2.count}</p>
                                                    <p className="text-zinc-400 text-sm">Level 2</p>
                                                    <p className="text-green-500 text-xs mt-1">5% Commission</p>
                                                </div>
                                                <div className="bg-zinc-900/50 border border-zinc-700 rounded-lg p-4 text-center">
                                                    <p className="text-white text-2xl font-bold">{userDetails.team.level3.count}</p>
                                                    <p className="text-zinc-400 text-sm">Level 3</p>
                                                    <p className="text-green-500 text-xs mt-1">3% Commission</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Earnings */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                                <Coins className="w-5 h-5 mr-2 text-gold" />
                                                Referral Earnings
                                            </h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="bg-zinc-900/50 rounded-lg p-4">
                                                    <p className="text-zinc-400 text-sm">Level 1</p>
                                                    <p className="text-gold font-mono">{userDetails.earnings.level1?.toFixed(6)} PIO</p>
                                                </div>
                                                <div className="bg-zinc-900/50 rounded-lg p-4">
                                                    <p className="text-zinc-400 text-sm">Level 2</p>
                                                    <p className="text-gold font-mono">{userDetails.earnings.level2?.toFixed(6)} PIO</p>
                                                </div>
                                                <div className="bg-zinc-900/50 rounded-lg p-4">
                                                    <p className="text-zinc-400 text-sm">Level 3</p>
                                                    <p className="text-gold font-mono">{userDetails.earnings.level3?.toFixed(6)} PIO</p>
                                                </div>
                                                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4">
                                                    <p className="text-gold text-sm">Total Earnings</p>
                                                    <p className="text-gold font-mono font-bold">{userDetails.earnings.total?.toFixed(6)} PIO</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 mt-4">
                                                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                                                    <p className="text-yellow-500 text-sm">Pending</p>
                                                    <p className="text-yellow-500 font-mono">{userDetails.earnings.pending?.toFixed(6)} PIO</p>
                                                </div>
                                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                                                    <p className="text-green-500 text-sm">Paid</p>
                                                    <p className="text-green-500 font-mono">{userDetails.earnings.paid?.toFixed(6)} PIO</p>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Purchase History */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                                <Package className="w-5 h-5 mr-2 text-gold" />
                                                Purchase History ({userDetails.orders.length})
                                            </h3>
                                            {userDetails.orders.length > 0 ? (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="border-b border-zinc-800">
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Date</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">USDT</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">PIO</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Bonus</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {userDetails.orders.map((order) => (
                                                                <tr key={order.id} className="border-b border-zinc-800/50">
                                                                    <td className="py-2 px-2 text-white text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                                                                    <td className="py-2 px-2 text-white font-mono text-sm">${order.usdt_amount}</td>
                                                                    <td className="py-2 px-2 text-gold font-mono text-sm">{order.total_pio?.toFixed(4)}</td>
                                                                    <td className="py-2 px-2 text-green-500 text-sm">{order.discount_percent > 0 ? `+${order.discount_percent}%` : '-'}</td>
                                                                    <td className="py-2 px-2">
                                                                        <span className={`text-xs px-2 py-1 rounded ${
                                                                            order.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                                                                            order.status.includes('failed') ? 'bg-red-500/20 text-red-500' :
                                                                            'bg-yellow-500/20 text-yellow-500'
                                                                        }`}>{order.status}</span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p className="text-zinc-500 text-center py-4">No purchases yet</p>
                                            )}
                                        </div>
                                        
                                        {/* Direct Team Members */}
                                        {userDetails.team.level1.count > 0 && (
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                                    <Users className="w-5 h-5 mr-2 text-gold" />
                                                    Direct Team Members ({userDetails.team.level1.count})
                                                </h3>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full">
                                                        <thead>
                                                            <tr className="border-b border-zinc-800">
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Wallet</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Joined</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">Spent</th>
                                                                <th className="text-left py-2 px-2 text-zinc-400 text-sm">PIO</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {userDetails.team.level1.members.slice(0, 10).map((member) => (
                                                                <tr key={member.id} className="border-b border-zinc-800/50">
                                                                    <td className="py-2 px-2 text-white font-mono text-sm">{member.wallet_address?.slice(0, 8)}...{member.wallet_address?.slice(-6)}</td>
                                                                    <td className="py-2 px-2 text-zinc-400 text-sm">{new Date(member.created_at).toLocaleDateString()}</td>
                                                                    <td className="py-2 px-2 text-white font-mono text-sm">${member.total_purchased_usdt?.toFixed(2) || '0.00'}</td>
                                                                    <td className="py-2 px-2 text-gold font-mono text-sm">{member.total_pio_received?.toFixed(4) || '0.0000'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminDashboard;
